import { Locales } from '@griply/types';
import { formatDurationLabel } from '@griply/utils';
import { format, isSameDay } from 'date-fns';
import { NLP } from 'shared/constants';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { SuggestionTriggers } from 'shared/types/nlp';
import { NewTagWithID, Tag } from 'shared/types/tag';
import { CreateTask } from 'shared/types/task';
import { TaskPriorityOptions } from 'shared/types/task-base';
import { Timeslot } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';
import { isNumber } from 'shared/utils/is-number';
import { getAllDatePatterns } from 'shared/utils/nlp/date-processing';
import {
  getTargetPatternContent,
  parseTargetToString,
} from 'shared/utils/nlp/frequency-processing';
import { getDisplayDate } from 'shared/utils/nlp/get-display-date';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { getWordIndexFromMatch } from 'shared/utils/nlp/get-word-index-for-match';
import {
  formatReminderToken,
  getReminderPatternString,
} from 'shared/utils/nlp/reminder-processing';
import {
  getRrulePatternContent,
  parseRruleToString,
} from 'shared/utils/nlp/rrule-processing';
import { syncMultiValueTokens } from 'shared/utils/nlp/sync-multi-value-tokens';
import { parseTime } from 'shared/utils/nlp/time-processing';
import { parseDateTime } from 'shared/utils/parse-date-time';

export type Options = {
  wordIds: WordId[];
  ignoredWordIds: ID[];
  goals: (Goal | NewGoalWithID)[];
  lifeAreas: LifeArea[];
  tags: (Tag | NewTagWithID)[];
  language: Locales;
  dateFormat: DateFormatOptions;
  timeFormat: TimeFormatOptions;
  createHabitAllowed: boolean;
  today: Timestamp;
};

const isIgnored = (
  match: RegExpMatchArray,
  wordIds: WordId[],
  ignoredWordIds: ID[],
) => {
  const wordIndex = getWordIndexFromMatch(match, wordIds);
  const wordId = wordIds[wordIndex]?.id;
  return wordId ? ignoredWordIds.includes(wordId) : false;
};

const processDate = (
  strValue: string,
  {
    date,
    dateFormat,
    wordIds,
    ignoredWordIds,
    allDatePatterns,
    rruleRegex,
    frequencyRegex,
    language,
  }: {
    date: Timestamp | null | undefined;
    dateFormat: DateFormatOptions;
    wordIds: WordId[];
    ignoredWordIds: ID[];
    allDatePatterns: string;
    rruleRegex: RegExp;
    frequencyRegex: RegExp;
    language: Locales;
  },
) => {
  // get all matching schedules and the last one
  const scheduleMatches = [
    ...strValue.matchAll(rruleRegex),
    ...strValue.matchAll(frequencyRegex),
  ]
    .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
    .sort((matchA, matchB) => (matchA?.index ?? 0) - (matchB?.index ?? 0));
  const lastScheduleMatch = scheduleMatches[scheduleMatches.length - 1];

  const dateRegex = new RegExp(
    `(?<!every\\s)(?<!elke\\s)(?<!{)\\b(${allDatePatterns})\\b`,
    'gi',
  );

  const activeDateMatches = [...strValue.matchAll(dateRegex)].filter(
    (match) => {
      if (
        isIgnored(match, wordIds, ignoredWordIds) ||
        !getDisplayDate(match[0], language, dateFormat, false)
      ) {
        return false;
      }

      // if there is a schedule, only consider dates after the last schedule match
      if (lastScheduleMatch) {
        const lastScheduleMatchEndIdx =
          lastScheduleMatch.index! + lastScheduleMatch[0].length;
        const matchIdx = match.index ?? -1;
        return matchIdx > lastScheduleMatchEndIdx;
      }

      return true;
    },
  );

  const lastDateMatch = activeDateMatches.pop();

  if (!date) {
    if (lastDateMatch) {
      const matchIndex = lastDateMatch.index!;
      const prefix = strValue.substring(0, matchIndex);
      const suffix = strValue.substring(matchIndex + lastDateMatch[0].length);
      return `${prefix.trimEnd()} ${suffix.trimStart()}`;
    }
    return strValue;
  }

  // Format the selected date in "24 Sep 2025" format.
  const month = date ? format(date, 'LLL').replace('.', '') : undefined;
  const dateString = date
    ? `${date.getDate()} ${month} ${date.getFullYear()}`
    : '';

  if (lastDateMatch) {
    const matchIndex = lastDateMatch.index!;
    const prefix = strValue.substring(0, matchIndex);
    const suffix = strValue.substring(matchIndex + lastDateMatch[0].length);
    return dateString
      ? `${prefix}${dateString}${suffix}`
      : `${prefix.trimEnd()} ${suffix.trimStart()}`;
  }

  if (dateString) {
    const cleanedInput = strValue
      .replace(new RegExp(NLP.pillSeparator, 'g'), ' ')
      .trim();
    return `${cleanedInput} ${dateString}${NLP.pillSeparator}`;
  }

  return strValue;
};

const processStartTime = (
  strValue: string,
  {
    timeslot,
    timeFormat,
    wordIds,
    ignoredWordIds,
  }: {
    timeslot: Timeslot;
    timeFormat: TimeFormatOptions;
    wordIds: WordId[];
    ignoredWordIds: ID[];
  },
) => {
  // Regex to find existing times (either single or range)
  const timeRegex = new RegExp(
    `(?<!-)${NLP.singleTimePatternString}(?!-)`,
    'gi',
  );
  const timeMatch = [...strValue.matchAll(timeRegex)]
    .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
    .pop();
  const timeRangeRegex = new RegExp(NLP.timeRangePatternString, 'gi');
  const timeRangeMatch = [...strValue.matchAll(timeRangeRegex)]
    .filter((match) => {
      if (isIgnored(match, wordIds, ignoredWordIds)) {
        return false;
      }
      if (match[0].includes('-')) {
        const parts = match[0].split('-');
        const start = parseTime(parts[0].trim());
        const end = parseTime(parts[1].trim());
        // Ensure both start and end times are valid before considering it a match
        if (!start || !end) {
          return false;
        }
        return end.hours * 60 + end.minutes > start.hours * 60 + start.minutes;
      }
      return parseTime(match[0]);
    })
    .pop();

  const lastMatch =
    (timeMatch?.index ?? -1) > (timeRangeMatch?.index ?? -1)
      ? timeMatch
      : timeRangeMatch;
  const lastMatchIsRange =
    lastMatch && lastMatch?.index === timeRangeMatch?.index;

  // If a time range (e.g., 14:00-15:00) is currently detected, update it with the new duration.
  if (
    isNumber(lastMatch?.index) &&
    lastMatchIsRange &&
    isNumber(timeslot.startTime)
  ) {
    if (isNumber(timeslot.duration) && timeslot.duration > 0) {
      const startDate = new Date(timeslot.startTime);
      const startHours = startDate.getUTCHours();
      const startMinutesNum = startDate.getUTCMinutes();

      const endDate = new Date(timeslot.startTime + timeslot.duration);
      const endHours = endDate.getUTCHours();
      const endMinutesNum = endDate.getUTCMinutes();

      let startString;
      let endString;
      const useAmPm = timeFormat !== TimeFormatOptions.Military;

      if (useAmPm) {
        let startHours12 = endHours % 12;
        if (startHours12 === 0) {
          startHours12 = 12;
        } // Handle midnight/noon
        const startAmpm = startHours >= 12 ? 'pm' : 'am';
        if (startMinutesNum === 0) {
          startString = `${startHours12}${startAmpm}`;
        } else {
          startString = `${startHours12}:${startMinutesNum.toString().padStart(2, '0')}${startAmpm}`;
        }
        let endHours12 = endHours % 12;
        if (endHours12 === 0) {
          endHours12 = 12;
        } // Handle midnight/noon
        const endAmpm = endHours >= 12 ? 'pm' : 'am';
        if (endMinutesNum === 0) {
          endString = `${endHours12}${endAmpm}`;
        } else {
          endString = `${endHours12}:${endMinutesNum.toString().padStart(2, '0')}${endAmpm}`;
        }
      } else {
        startString = `${startHours.toString().padStart(2, '0')}:${startMinutesNum.toString().padStart(2, '0')}`;
        endString = `${endHours.toString().padStart(2, '0')}:${endMinutesNum.toString().padStart(2, '0')}`;
      }

      const newTimeRange = `${startString}-${endString}`;

      // Replace the last found time range with the new, updated one.
      return (
        strValue.substring(0, lastMatch.index) +
        newTimeRange +
        strValue.substring(lastMatch.index + lastMatch[0].length)
      );
    }
  } else {
    const time = timeslot.startTime;
    const startTimeKeyWord = isNumber(time)
      ? // when processing the startTime, we need to lowercase the time string
        // and when it is standard time, we need to remove the space
        `${parseDateTime(time, timeFormat).toLowerCase().replace(' ', '')}`
      : '';

    if (lastMatch) {
      const matchIndex = lastMatch.index!;
      const prefix = strValue.substring(0, matchIndex);
      const suffix = strValue.substring(matchIndex + lastMatch[0].length);
      return startTimeKeyWord
        ? `${prefix}${startTimeKeyWord}${suffix}`
        : `${prefix.trimEnd()} ${suffix.trimStart()}`;
    }
    if (startTimeKeyWord) {
      const cleanedInput = strValue
        .replace(new RegExp(NLP.pillSeparator, 'g'), ' ')
        .trim();
      return `${cleanedInput} ${startTimeKeyWord}${NLP.pillSeparator}`;
    }
  }
  return strValue;
};

const processDuration = (
  strValue: string,
  {
    timeslot,
    timeFormat,
    wordIds,
    ignoredWordIds,
  }: {
    timeslot: Timeslot;
    timeFormat: TimeFormatOptions;
    wordIds: WordId[];
    ignoredWordIds: ID[];
  },
) => {
  // regex to find duration and time range
  const durationRegex = /\b(?:\d+h(?:\s*\d+m)?|\d+m)\b/gi;
  const durationMatch = [...strValue.matchAll(durationRegex)]
    .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
    .pop();
  const timeRangeRegex = new RegExp(NLP.timeRangePatternString, 'gi');
  const timeRangeMatch = [...strValue.matchAll(timeRangeRegex)]
    .filter((match) => {
      if (isIgnored(match, wordIds, ignoredWordIds)) {
        return false;
      }
      if (match[0].includes('-')) {
        const parts = match[0].split('-');
        const start = parseTime(parts[0].trim());
        const end = parseTime(parts[1].trim());
        // Ensure both start and end times are valid before considering it a match
        if (!start || !end) {
          return false;
        }
        return end.hours * 60 + end.minutes > start.hours * 60 + start.minutes;
      }
      return parseTime(match[0]);
    })
    .pop();

  const lastMatch =
    (durationMatch?.index ?? -1) > (timeRangeMatch?.index ?? -1)
      ? durationMatch
      : timeRangeMatch;

  const lastMatchIsRange =
    lastMatch && lastMatch?.index === timeRangeMatch?.index;

  // If a time range (e.g., 14:00-15:00) is currently detected, update it with the new duration.
  if (
    isNumber(lastMatch?.index) &&
    lastMatchIsRange &&
    isNumber(timeslot.startTime)
  ) {
    if (isNumber(timeslot.duration) && timeslot.duration > 0) {
      const startDate = new Date(timeslot.startTime);
      const startHours = startDate.getUTCHours();
      const startMinutesNum = startDate.getUTCMinutes();

      const endDate = new Date(timeslot.startTime + timeslot.duration);
      const endHours = endDate.getUTCHours();
      const endMinutesNum = endDate.getUTCMinutes();

      let startString;
      let endString;
      const useAmPm = timeFormat !== TimeFormatOptions.Military;

      if (useAmPm) {
        let startHours12 = endHours % 12;
        if (startHours12 === 0) {
          startHours12 = 12;
        } // Handle midnight/noon
        const startAmpm = startHours >= 12 ? 'pm' : 'am';
        if (startMinutesNum === 0) {
          startString = `${startHours12}${startAmpm}`;
        } else {
          startString = `${startHours12}:${startMinutesNum.toString().padStart(2, '0')}${startAmpm}`;
        }
        let endHours12 = endHours % 12;
        if (endHours12 === 0) {
          endHours12 = 12;
        } // Handle midnight/noon
        const endAmpm = endHours >= 12 ? 'pm' : 'am';
        if (endMinutesNum === 0) {
          endString = `${endHours12}${endAmpm}`;
        } else {
          endString = `${endHours12}:${endMinutesNum.toString().padStart(2, '0')}${endAmpm}`;
        }
      } else {
        startString = `${startHours.toString().padStart(2, '0')}:${startMinutesNum.toString().padStart(2, '0')}`;
        endString = `${endHours.toString().padStart(2, '0')}:${endMinutesNum.toString().padStart(2, '0')}`;
      }

      const newTimeRange = `${startString}-${endString}`;

      // Replace the last found time range with the new, updated one.
      return (
        strValue.substring(0, lastMatch.index) +
        newTimeRange +
        strValue.substring(lastMatch.index + lastMatch[0].length)
      );
    }
  } else {
    // Fallback: If no time range exists, just replace or append the duration string (e.g., "2h").
    const durationRegex = /\b(?:\d+h(?:\s*\d+m)?|\d+m)\b/gi;
    const activeDurationMatches = [...strValue.matchAll(durationRegex)].filter(
      (match) => !isIgnored(match, wordIds, ignoredWordIds),
    );
    const lastActiveDurationMatch = activeDurationMatches.pop();

    const duration = isNumber(timeslot.duration)
      ? formatDurationLabel(timeslot.duration, false)
      : '';
    if (lastActiveDurationMatch) {
      // Replace the last found duration with the new one.
      const matchIndex = lastActiveDurationMatch.index!;
      const prefix = strValue.substring(0, matchIndex);
      const suffix = strValue.substring(
        matchIndex + lastActiveDurationMatch[0].length,
      );
      return duration
        ? `${prefix}${duration}${suffix}`
        : `${prefix.trimEnd()} ${suffix.trimStart()}`;
    }
    // Append the new duration.
    return `${strValue.replace(/\u00A0/g, ' ').trim()} ${duration}\u00A0`;
  }

  return strValue;
};

const processTimeslot = (
  strValue: string,
  {
    timeslot,
    timeFormat,
    wordIds,
    ignoredWordIds,
  }: {
    timeslot: Timeslot | null | undefined;
    timeFormat: TimeFormatOptions;
    wordIds: WordId[];
    ignoredWordIds: ID[];
  },
) => {
  // regex to find startTime, duration and time range
  const timeRegex = new RegExp(
    `(?<!-)${NLP.singleTimePatternString}(?!-)`,
    'gi',
  );
  const timeMatch = [...strValue.matchAll(timeRegex)]
    .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
    .pop();
  const durationRegex = /\b(?:\d+h(?:\s*\d+m)?|\d+m)\b/gi;
  const durationMatch = [...strValue.matchAll(durationRegex)]
    .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
    .pop();
  const timeRangeRegex = new RegExp(NLP.timeRangePatternString, 'gi');
  const timeRangeMatch = [...strValue.matchAll(timeRangeRegex)]
    .filter((match) => {
      if (isIgnored(match, wordIds, ignoredWordIds)) {
        return false;
      }
      if (match[0].includes('-')) {
        const parts = match[0].split('-');
        const start = parseTime(parts[0].trim());
        const end = parseTime(parts[1].trim());
        // Ensure both start and end times are valid before considering it a match
        if (!start || !end) {
          return false;
        }
        return end.hours * 60 + end.minutes > start.hours * 60 + start.minutes;
      }
      return parseTime(match[0]);
    })
    .pop();

  const orderedMatches = [timeRangeMatch, durationMatch, timeMatch].sort(
    (a, b) => (a?.index ?? -1) - (b?.index ?? -1),
  );
  const middleMatch = orderedMatches[1];
  const lastMatch = orderedMatches[2];

  if (!timeslot) {
    if (lastMatch && lastMatch?.index === timeRangeMatch?.index) {
      const matchIndex = lastMatch.index!;
      const prefix = strValue.substring(0, matchIndex);
      const suffix = strValue.substring(matchIndex + lastMatch[0].length);
      return `${prefix.trimEnd()} ${suffix.trimStart()}`;
    }

    if (
      lastMatch &&
      (lastMatch?.index === durationMatch?.index ||
        lastMatch?.index === timeMatch?.index)
    ) {
      const matchIndex = lastMatch.index!;
      const prefix = strValue.substring(0, matchIndex);
      const suffix = strValue.substring(matchIndex + lastMatch[0].length);
      const newStrValue = `${prefix}${suffix}`;

      if (
        middleMatch &&
        (middleMatch.index === timeMatch?.index ||
          middleMatch.index === durationMatch?.index)
      ) {
        const matchIndex = middleMatch?.index!;
        const prefix = newStrValue.substring(0, matchIndex);
        const suffix = newStrValue.substring(
          matchIndex + middleMatch[0].length,
        );
        return `${prefix.trimEnd()} ${suffix.trimStart()}`;
      }
    }

    // no time match means no changes
    return strValue;
  }

  let newStr = strValue;
  // if startTime or duration is missing, a timeRange is not possible
  if (
    lastMatch &&
    lastMatch?.index === timeRangeMatch?.index &&
    !(isNumber(timeslot.startTime) && isNumber(timeslot.duration))
  ) {
    const matchIndex = lastMatch.index!;
    const prefix = strValue.substring(0, matchIndex);
    const suffix = strValue.substring(matchIndex + lastMatch[0].length);
    return `${prefix.trimEnd()} ${suffix.trimStart()}`;
  }

  newStr = processStartTime(newStr, {
    timeslot,
    timeFormat,
    wordIds,
    ignoredWordIds,
  });

  return processDuration(newStr, {
    timeslot,
    timeFormat,
    wordIds,
    ignoredWordIds,
  });
};

export const processNewTaskVariablesInString = (
  name: string,
  taskVariables: Partial<CreateTask>,
  {
    wordIds,
    ignoredWordIds,
    goals,
    lifeAreas,
    tags,
    language,
    dateFormat,
    timeFormat,
    createHabitAllowed,
    today,
  }: Options,
) => {
  // prepare regexes
  const allDatePatterns = getAllDatePatterns(language);
  const rrulePatternContent = getRrulePatternContent(language);
  const rruleRegex = new RegExp(`\\b(${rrulePatternContent})\\b`, 'gi');

  const frequencyPatterContent = getTargetPatternContent(language);
  const frequencyRegex = new RegExp(`\\b(${frequencyPatterContent})\\b`, 'gi');

  return Object.keys(taskVariables).reduce((acc, key) => {
    const _key = key as keyof typeof taskVariables;

    if (_key === 'goalId' || _key === 'lifeAreaId') {
      const isRemoval =
        taskVariables.goalId === null && taskVariables.lifeAreaId === null;

      // when the linked item is removed, remove it from the text
      if (isRemoval) {
        const lastActiveMatch = [
          ...acc.matchAll(new RegExp(`~${NLP.goalSubPattern}\\b`, 'g')),
        ]
          .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
          .pop();

        if (lastActiveMatch) {
          const matchIndex = lastActiveMatch.index!;
          const prefix = acc.substring(0, matchIndex);
          const suffix = acc.substring(matchIndex + lastActiveMatch[0].length);
          return `${prefix.trimEnd()} ${suffix.trimStart()}`;
        }
      }

      const goal =
        _key === 'goalId'
          ? goals.find(({ id }) => id === taskVariables.goalId)
          : undefined;
      const lifeArea =
        _key === 'lifeAreaId'
          ? lifeAreas.find(({ id }) => id === taskVariables.lifeAreaId)
          : undefined;
      const linkedItem = goal ?? lifeArea;

      if (!linkedItem) {
        return acc;
      }

      const lastActiveMatch = [
        ...acc.matchAll(new RegExp(`~${NLP.goalSubPattern}\\b`, 'g')),
      ]
        .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
        .pop();

      if (lastActiveMatch) {
        // If an *active* goal already exists in the text, replace the last occurrence of it with the new goal.
        // This preserves any text that came after the original goal.
        const matchIndex = lastActiveMatch.index!;
        const prefix = acc.substring(0, matchIndex);
        const suffix = acc.substring(matchIndex + lastActiveMatch[0].length);
        return `${prefix}${SuggestionTriggers.LinkedItem}${linkedItem.name}${suffix}`;
      }

      // If no active goal exists, add the new one to the end of the input, ensuring there's a preceding space
      // and a trailing non-breaking space for better cursor handling.
      const cleanedInput = acc
        .replace(new RegExp(NLP.pillSeparator, 'g'), ' ')
        .trim();
      return cleanedInput
        ? `${cleanedInput} ${SuggestionTriggers.LinkedItem}${linkedItem.name}${NLP.whitespace}`
        : `${SuggestionTriggers.LinkedItem}${linkedItem.name}${NLP.whitespace}`;
    }

    if (_key === 'tagIds') {
      // get all tags that should be in the name
      const prefixedFilteredTags = tags.reduce<(Tag | NewTagWithID)[]>(
        (acc, tag) => {
          if (!taskVariables.tagIds?.includes(tag.id)) {
            return acc;
          }
          acc.push({ ...tag, name: `${SuggestionTriggers.Tag}${tag.name}` });
          return acc;
        },
        [],
      );
      return syncMultiValueTokens(acc, {
        regex: new RegExp(NLP.tagPattern, 'gi'),
        wantedTokens: prefixedFilteredTags.map(({ name }) => name),
        trailing: NLP.whitespace,
        wordIds,
        ignoredWordIds,
      });
    }

    if (_key === 'endStrategy') {
      const lastScheduleMatch = [
        ...acc.matchAll(rruleRegex),
        ...acc.matchAll(frequencyRegex),
      ]
        .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
        .sort((matchA, matchB) => (matchA?.index ?? 0) - (matchB?.index ?? 0))
        .pop();

      // if there is an active schedule, we don't want to mess with the dates
      if (lastScheduleMatch && !taskVariables.endStrategy) {
        return acc;
      }

      return processDate(acc, {
        date: taskVariables.endStrategy?.deadline,
        language,
        dateFormat,
        wordIds,
        ignoredWordIds,
        allDatePatterns,
        rruleRegex,
        frequencyRegex,
      });
    }

    if (_key === 'timeslot') {
      return processTimeslot(acc, {
        timeslot: taskVariables.timeslot,
        timeFormat,
        wordIds,
        ignoredWordIds,
      });
    }

    if (_key === 'schedules') {
      // get the last matching schedule
      const lastScheduleMatch = [
        ...acc.matchAll(rruleRegex),
        ...acc.matchAll(frequencyRegex),
      ]
        .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
        .sort((matchA, matchB) => (matchA?.index ?? 0) - (matchB?.index ?? 0))
        .pop();

      // handle removal of schedules
      if (!taskVariables.schedules?.length) {
        if (lastScheduleMatch) {
          const matchIndex = lastScheduleMatch.index!;
          const prefix = acc.substring(0, matchIndex);
          const suffix = acc.substring(
            matchIndex + lastScheduleMatch[0].length,
          );
          return `${prefix.trimEnd()} ${suffix.trimStart()}`;
        }

        // if there is no match, nothing has to be removed
        return acc;
      }

      const deadlineMatch = [
        ...acc.matchAll(new RegExp(`{(${allDatePatterns})}`, 'gi')),
      ]
        .filter((m) => getDisplayDate(m[1], language, dateFormat, true))
        .pop();

      // handle schedule being set
      const schedule =
        taskVariables.schedules[taskVariables.schedules.length - 1];

      if (schedule.frequency && !createHabitAllowed) {
        return acc;
      }

      const scheduleString = schedule.frequency
        ? parseTargetToString(schedule.frequency, language)
        : parseRruleToString(schedule.rrule.format, language).trim();
      // if the last schedule string is after the deadline string, replace that schedule string
      if (
        lastScheduleMatch &&
        (deadlineMatch?.index ?? -1) < lastScheduleMatch.index!
      ) {
        const matchIndex = lastScheduleMatch.index!;
        const prefix = acc.substring(0, matchIndex);
        const suffix = acc.substring(matchIndex + lastScheduleMatch[0].length);
        const newStr = `${prefix}${scheduleString}${suffix}`;
        const timeslotStr = processTimeslot(newStr, {
          timeslot: schedule.timeslot,
          timeFormat,
          wordIds,
          ignoredWordIds,
        });
        return processDate(timeslotStr, {
          date: isSameDay(schedule.startDate, today)
            ? null
            : schedule.startDate,
          dateFormat,
          allDatePatterns,
          rruleRegex,
          frequencyRegex,
          language,
          wordIds,
          ignoredWordIds,
        });
      }

      // otherwise place the schedule string at the end
      const cleanedInput = acc
        .replace(new RegExp(NLP.pillSeparator, 'g'), ' ')
        .trim();
      const scheduleStr = `${cleanedInput} ${scheduleString}${NLP.pillSeparator}`;
      const timeslotStr = processTimeslot(scheduleStr, {
        timeslot: schedule.timeslot,
        timeFormat,
        wordIds,
        ignoredWordIds,
      });
      return processDate(timeslotStr, {
        date: isSameDay(schedule.startDate, today) ? null : schedule.startDate,
        dateFormat,
        allDatePatterns,
        rruleRegex,
        frequencyRegex,
        language,
        wordIds,
        ignoredWordIds,
      });
    }

    if (_key === 'priority') {
      const priorityRegex = new RegExp(`\\bp[1-3]\\b|!![1-3]`, 'gi');
      const lastActivePriorityMatch = [...acc.matchAll(priorityRegex)]
        .filter((match) => !isIgnored(match, wordIds, ignoredWordIds))
        .pop();

      const priorityKeywords: Record<TaskPriorityOptions, string> = {
        [TaskPriorityOptions.High]: '!!1',
        [TaskPriorityOptions.Medium]: '!!2',
        [TaskPriorityOptions.Low]: '!!3',
        [TaskPriorityOptions.None]: '!!3',
      };
      const priorityKeyword = priorityKeywords[taskVariables.priority!] ?? '';

      if (lastActivePriorityMatch) {
        const matchIndex = lastActivePriorityMatch.index!;
        const prefix = acc.substring(0, matchIndex);
        const suffix = acc.substring(
          matchIndex + lastActivePriorityMatch[0].length,
        );

        return priorityKeyword
          ? `${prefix}${priorityKeyword}${suffix}`
          : `${prefix.trimEnd()} ${suffix.trimStart()}`;
      }

      if (priorityKeyword) {
        const cleanedInput = acc
          .replace(new RegExp(NLP.pillSeparator, 'g'), ' ')
          .trim();
        return `${cleanedInput} ${priorityKeyword}${NLP.pillSeparator}`;
      }
    }

    if (_key === 'deadlineDeadline') {
      // Regex needs to find dates wrapped in curly braces
      const deadlineRegex = new RegExp(`{(${allDatePatterns})}`, 'gi');
      const lastActiveDeadlineMatch = [...acc.matchAll(deadlineRegex)]
        .filter(
          (match) =>
            getDisplayDate(match[1], language, dateFormat, true) &&
            !isIgnored(match, wordIds, ignoredWordIds),
        )
        .pop();

      // Format the selected date in "24 Sep 2025" format.
      const date = taskVariables.deadlineDeadline;
      const month = date ? format(date, 'LLL').replace('.', '') : undefined;
      const dateString = date
        ? `${date.getDate()} ${month} ${date.getFullYear()}`
        : undefined;
      const deadlineKeyword = date ? `{${dateString}}` : '';

      if (lastActiveDeadlineMatch) {
        const matchIndex = lastActiveDeadlineMatch.index!;
        const prefix = acc.substring(0, matchIndex);
        const suffix = acc.substring(
          matchIndex + lastActiveDeadlineMatch[0].length,
        );
        return deadlineKeyword
          ? `${prefix}${deadlineKeyword}${suffix}`
          : `${prefix.trimEnd()} ${suffix.trimStart()}`;
      }

      if (deadlineKeyword) {
        const cleanedInput = acc
          .replace(new RegExp(NLP.pillSeparator, 'g'), ' ')
          .trim();
        return `${cleanedInput} ${deadlineKeyword}${NLP.pillSeparator}`;
      }
    }

    if (_key === 'reminders') {
      // Reminders with no text form (end-based, or firing after the start) keep
      // whatever token the input already holds instead of being dropped.
      const wantedTokens = (taskVariables.reminders ?? [])
        .map((reminder) => formatReminderToken(reminder, timeFormat, language))
        .filter(Boolean) as string[];

      return syncMultiValueTokens(acc, {
        regex: new RegExp(getReminderPatternString(language), 'gi'),
        wantedTokens,
        trailing: NLP.pillSeparator,
        wordIds,
        ignoredWordIds,
      });
    }

    return acc;
  }, name);
};
